import { useState, useEffect } from 'react';
import './timeline.css';
import { ChevronRight, ChevronDown, User, Calendar, Info } from 'react-feather';
import { Modal, Button, Spin, Alert, List, Card, Tag, Divider, message, Checkbox, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { ActionGetAvailableRooms, ActionChangeRoom, ActionUpgradeRoom } from '../../../store/Actions/BookingAction';
import { ActionGetBookingsDashboard } from '../../../store/Actions/MasterDataAction';
import moment from 'moment';

// Helper to generate dates
const getDaysArray = (start, end) => {
    const arr = [];
    for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
        arr.push(new Date(dt));
    }
    return arr;
};

// Colors for bookings - ACTIVE status (solid, full opacity)
const ACTIVE_STYLES = {
    MALE: { bg: '#3b82f6', border: '#2563eb', text: '#ffffff' }, // Solid Blue
    FEMALE: { bg: '#ec4899', border: '#db2777', text: '#ffffff' }, // Solid Pink
    GROUP: { bg: '#8b5cf6', border: '#7c3aed', text: '#ffffff' }, // Solid Purple
    DEFAULT: { bg: '#10b981', border: '#059669', text: '#ffffff' } // Solid Green
};

// Colors for bookings - UPCOMING status (lighter, semi-transparent)
const UPCOMING_STYLES = {
    MALE: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' }, // Light Blue
    FEMALE: { bg: '#fce7f3', border: '#f9a8d4', text: '#be185d' }, // Light Pink
    GROUP: { bg: '#ede9fe', border: '#c4b5fd', text: '#6b21a8' }, // Light Purple
    DEFAULT: { bg: '#d1fae5', border: '#6ee7b7', text: '#047857' } // Light Green
};

const TimelineScheduler = ({ filterStartDate, filterEndDate }) => {
    // 1. Setup Date Range
    // const today = new Date();
    const today = new Date();
    const todayNorm = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    let startDate, endDate;

    if (filterStartDate) {
        startDate = new Date(filterStartDate);
        startDate.setHours(0, 0, 0, 0);
    } else {
        startDate = new Date(todayNorm);
    }

    if (filterEndDate) {
        endDate = new Date(filterEndDate);
        endDate.setHours(0, 0, 0, 0);
    } else {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 21); // Default 3 weeks
    }

    const dates = getDaysArray(startDate, endDate);

    const { bookingsDashboard } = useSelector((state) => state.MasterData);
    const { availableRooms, availableRoomsLoading, availableRoomsError } = useSelector((state) => state.BookingManagement);
    const dispatch = useDispatch();

    // 2. Map API Data
    const [groups, setGroups] = useState([]);

    useEffect(() => {
        if (bookingsDashboard && bookingsDashboard.length > 0) {

            const mappedGroups = bookingsDashboard.map(roomType => ({
                id: roomType.roomTypeId,
                name: roomType.roomTypeName,
                totalRooms: roomType.totalRooms,
                collapsed: false,
                rooms: roomType.rooms.map(room => {
                    // Determine if room should be treated as shared
                    // A room is shared if capacity > 1 AND has at least one SHARED booking
                    const hasSharedBookings = room.bookings.some(b => b.occupancyMode === 'SHARED');
                    const isShared = room.maxCapacity > 1 && hasSharedBookings;

                    // First pass: normalize booking dates and prepare data
                    const normalizedBookings = room.bookings.map((booking, index) => {
                        const startDate = new Date(booking.admissionDate);
                        startDate.setHours(0, 0, 0, 0);

                        const dischargeDate = new Date(booking.dischargeDate);
                        dischargeDate.setHours(0, 0, 0, 0);

                        const span = Math.max(1, Math.ceil((dischargeDate - startDate) / (1000 * 60 * 60 * 24)));

                        // Determine Color/Style based on status and group
                        let style;
                        const isActive = booking.status === 'ACTIVE';
                        const gender = booking.guestGender ? booking.guestGender.toLowerCase() : '';

                        // Use single color for all group bookings
                        if (booking.isGroupBooking) {
                            style = isActive ? ACTIVE_STYLES.GROUP : UPCOMING_STYLES.GROUP;
                        } else if (gender === 'male') {
                            style = isActive ? ACTIVE_STYLES.MALE : UPCOMING_STYLES.MALE;
                        } else if (gender === 'female') {
                            style = isActive ? ACTIVE_STYLES.FEMALE : UPCOMING_STYLES.FEMALE;
                        } else {
                            style = isActive ? ACTIVE_STYLES.DEFAULT : UPCOMING_STYLES.DEFAULT;
                        }

                        // Example output:
                        // {
                        //     "checkIn": "2026-01-12T00:00:00.000Z",
                        //     "checkOut": "2026-01-14T00:00:00.000Z",
                        //     "checkInReadable": "Sun, Jan 12, 2026",
                        //     "checkOutReadable": "Tue, Jan 14, 2026"
                        // }
                        return {
                            id: booking.bookingId,
                            guest: booking.guestName,
                            bedIndex: 0, // Will be assigned below
                            status: booking.status,
                            amount: '',
                            start: startDate,
                            end: new Date(startDate.getTime() + (span - 1) * 24 * 60 * 60 * 1000),
                            span: span,
                            style: style,
                            checkIn: booking.admissionDate ? moment(booking.admissionDate).format('ddd, MMM D, YYYY') : 'N/A',
                            checkOut: booking.dischargeDate ? moment(booking.dischargeDate).format('ddd, MMM D, YYYY') : 'N/A',
                            notes: `Occupancy: ${booking.occupancyMode}`,
                            occupancyMode: booking.occupancyMode,
                            occupancyMaxPersons: booking.occupancyMaxPersons,
                            isGroupBooking: booking.isGroupBooking,
                            orderUniqueId: booking.orderUniqueId,
                            guestGender: booking.guestGender,
                            phone: booking.phone || 'N/A',
                            email: booking.email || 'N/A',
                            admissionDate: booking.admissionDate,
                            dischargeDate: booking.dischargeDate,
                            roomTypeId: roomType.roomTypeId,
                            roomTypeName: roomType.roomTypeName
                        };
                    });

                    // Second pass: assign bedIndex intelligently
                    // Sort bookings by start date to process chronologically
                    const sortedBookings = [...normalizedBookings].sort((a, b) => a.start - b.start);

                    sortedBookings.forEach(booking => {
                        if (booking.occupancyMode === 'NON-SHARED') {
                            // NON-SHARED bookings always occupy bedIndex 0 (entire room)
                            booking.bedIndex = 0;
                        } else if (booking.occupancyMode === 'SHARED') {
                            // For SHARED bookings, find the first available bed that doesn't conflict
                            let assignedBed = -1;

                            for (let bedIdx = 0; bedIdx < room.maxCapacity; bedIdx++) {
                                // Check if this bed is available for the booking's date range
                                const hasConflict = sortedBookings.some(otherBooking => {
                                    // Skip self
                                    if (otherBooking.id === booking.id) return false;

                                    // Skip if not assigned to this bed yet
                                    if (otherBooking.bedIndex !== bedIdx) return false;

                                    // Check for date overlap
                                    const overlap = !(booking.end < otherBooking.start || booking.start > otherBooking.end);
                                    return overlap;
                                });

                                if (!hasConflict) {
                                    assignedBed = bedIdx;
                                    break;
                                }
                            }

                            // Assign the found bed, or default to 0 if no bed found (shouldn't happen in valid data)
                            booking.bedIndex = assignedBed >= 0 ? assignedBed : 0;
                        }
                    });

                    // Calculate occupied beds count
                    const occupiedBeds = new Set();
                    normalizedBookings.forEach(booking => {
                        // Check if booking overlaps with today
                        const bookingActive = todayNorm >= booking.start && todayNorm <= booking.end;
                        if (bookingActive) {
                            if (booking.occupancyMode === 'NON-SHARED') {
                                // NON-SHARED occupies all beds
                                for (let i = 0; i < room.maxCapacity; i++) {
                                    occupiedBeds.add(i);
                                }
                            } else {
                                occupiedBeds.add(booking.bedIndex);
                            }
                        }
                    });

                    return {
                        id: room.roomId,
                        name: room.roomNo,
                        type: room.roomStatus,
                        isShared: isShared,
                        capacity: room.maxCapacity,
                        occupiedCount: occupiedBeds.size,
                        bookings: normalizedBookings
                    };
                })
            }));
            setGroups(mappedGroups);
        }
    }, [bookingsDashboard]);

    const toggleGroup = (groupId) => {
        setGroups(groups.map(g =>
            g.id === groupId ? { ...g, collapsed: !g.collapsed } : g
        ));
    };

    const getBookingOnDate = (room, date, bedIndex = 0) => {
        return room.bookings.find(b => {
            // If room is shared, check if booking matches the current bedIndex
            if (room.isShared && (b.bedIndex ?? 0) !== bedIndex) {
                return false;
            }

            const bStart = b.start.getTime();
            const bEnd = new Date(b.start);
            bEnd.setDate(bEnd.getDate() + b.span - 1);
            const eTime = bEnd.getTime();

            const dTime = date.getTime();

            return dTime >= bStart && dTime <= eTime;
        });
    };

    const isWeekend = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const isToday = (date) => {
        return date.getTime() === todayNorm.getTime();
    };

    // Modal Logic
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isAvailableRoomsModalVisible, setIsAvailableRoomsModalVisible] = useState(false);
    const [isUpgradeMode, setIsUpgradeMode] = useState(false);
    const [memberDaysSelection, setMemberDaysSelection] = useState({}); // { `${roomTypeId}_${occupancyModeId}`: boolean }

    const handleUseMemberDaysChange = (roomTypeId, occupancyModeId, checked) => {
        const key = `${roomTypeId}_${occupancyModeId}`;
        setMemberDaysSelection(prev => ({
            ...prev,
            [key]: checked
        }));
    };

    const handleBookingClick = (booking, roomName) => {
        setSelectedBooking({ ...booking, roomName });
        setIsModalVisible(true);
    };

    const handleChangeRoom = () => {
        if (selectedBooking?.id) {
            setIsUpgradeMode(false);
            dispatch(ActionGetAvailableRooms({
                bookingId: selectedBooking.id,
                isUpgrade: false,
                admissionDate: selectedBooking.admissionDate ? moment(selectedBooking.admissionDate).format('YYYY-MM-DD') : null,
                dischargeDate: selectedBooking.dischargeDate ? moment(selectedBooking.dischargeDate).format('YYYY-MM-DD') : null,
                roomTypeId: selectedBooking.roomTypeId
            }));
            setIsAvailableRoomsModalVisible(true);
        }
    };

    const handleUpgrade = () => {
        if (selectedBooking?.id) {
            setIsUpgradeMode(true);
            dispatch(ActionGetAvailableRooms({
                bookingId: selectedBooking.id,
                isUpgrade: true,
                admissionDate: selectedBooking.admissionDate ? moment(selectedBooking.admissionDate).format('YYYY-MM-DD') : null,
                dischargeDate: selectedBooking.dischargeDate ? moment(selectedBooking.dischargeDate).format('YYYY-MM-DD') : null
            }));
            setIsAvailableRoomsModalVisible(true);
        }
    };

    const handleCloseAvailableRoomsModal = () => {
        setIsAvailableRoomsModalVisible(false);
    };

    const handleRoomSelection = (room, occupancyGroup = null, option = null) => {
        const roomName = room.roomNo || 'this room';
        const bookingId = selectedBooking?.id;

        if (!bookingId) {
            message.error('Booking ID not found');
            return;
        }

        if (isUpgradeMode) {
            // Upgrade mode
            const upgradeKey = `${option.roomTypeId}_${occupancyGroup.occupancyModeId}`;
            const useMemberDays = memberDaysSelection[upgradeKey] || false;

            let costDisplay = (
                <p>Additional Cost: <strong>{option.currency} {option.priceDelta}</strong></p>
            );

            if (useMemberDays) {
                if (option.memberDayCalculation) {
                    costDisplay = (
                        <div>
                            <p>Cost in Days: <strong>{option.memberDayCalculation.daysRequired} Member Days</strong></p>
                            <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                ({option.memberDayCalculation.daysRequired} days for {option.memberDayCalculation.stayDuration} night(s) stay)
                            </p>
                        </div>
                    );
                }
            }

            Modal.confirm({
                title: 'Confirm Room Upgrade',
                content: (
                    <div>
                        <p>Are you sure you want to upgrade to <strong>{roomName}</strong>?</p>
                        <p>Room Type: <strong>{option.roomTypeName}</strong></p>
                        <p>Occupancy: <strong>{occupancyGroup.occupancyCode}</strong></p>
                        {costDisplay}
                        {useMemberDays && <p style={{ color: '#16a34a', fontWeight: '600' }}>Using Member Days</p>}
                    </div>
                ),
                zIndex: 10001,
                okText: 'Confirm Upgrade',
                cancelText: 'Cancel',
                onOk: () => {
                    dispatch(ActionUpgradeRoom({
                        bookingId,
                        newRoomId: room._id,
                        newRoomTypeId: option.roomTypeId,
                        newOccupancyModeId: occupancyGroup.occupancyModeId,
                        useMemberDays: useMemberDays
                    }, (response, error) => {
                        if (error) {
                            message.error(error?.response?.data?.message || 'Failed to upgrade room');
                        } else {
                            message.success(`Room upgraded successfully to ${roomName}!`);
                            setIsAvailableRoomsModalVisible(false);
                            setIsModalVisible(false);
                            // Refresh the dashboard to show updated room assignments
                            dispatch(ActionGetBookingsDashboard({ Query: `?startDate=${filterStartDate || ''}&endDate=${filterEndDate || ''}` }));
                        }
                    }));
                }
            });
        } else {
            // Change room mode
            Modal.confirm({
                title: 'Confirm Room Change',
                content: (
                    <div>
                        <p>Are you sure you want to change to <strong>{roomName}</strong>?</p>
                        <p style={{ fontSize: '0.9rem', color: '#64748b' }}>This will move the booking to the selected room.</p>
                    </div>
                ),
                zIndex: 10001,
                okText: 'Confirm Change',
                cancelText: 'Cancel',
                onOk: () => {
                    dispatch(ActionChangeRoom({
                        bookingId,
                        newRoomId: room._id
                    }, (response, error) => {
                        if (error) {
                            message.error(error?.response?.data?.message || 'Failed to change room');
                        } else {
                            message.success(`Room changed successfully to ${roomName}!`);
                            setIsAvailableRoomsModalVisible(false);
                            setIsModalVisible(false);
                            // Refresh the dashboard to show updated room assignments
                            dispatch(ActionGetBookingsDashboard({ Query: `?startDate=${filterStartDate || ''}&endDate=${filterEndDate || ''}` }));
                        }
                    }));
                }
            });
        }
    };

    const renderBookingPill = (booking, date, room) => {
        if (!booking) return null;

        // Determine if we should render the pill on this specific date
        // 1. If it's the actual start date of the booking
        const isBookingStart = booking.start.getTime() === date.getTime();

        // 2. If the booking started BEFORE the visible timeline, we render it on the FIRST visible day (startDate)
        const isTimelineStart = date.getTime() === startDate.getTime();
        const bookingStartedBeforeTimeline = booking.start < startDate;

        const shouldRender = isBookingStart || (isTimelineStart && bookingStartedBeforeTimeline);

        if (shouldRender) {
            const style = booking.style || { bg: '#ccc', text: '#333', border: '#999' };

            // Calculate display span
            // If rendering from start, use full span
            // If rendering from timeline start (truncated), calculate remaining span
            let displaySpan = booking.span;
            if (isTimelineStart && bookingStartedBeforeTimeline) {
                const remainingTime = booking.end.getTime() - startDate.getTime();
                // Add 1 day because span includes the start day itself
                displaySpan = Math.max(1, Math.ceil(remainingTime / (1000 * 60 * 60 * 24)) + 1);
            }

            // Gender icon
            const genderIcon = booking.guestGender?.toLowerCase() === 'male' ? '♂' :
                booking.guestGender?.toLowerCase() === 'female' ? '♀' : '';

            return (
                <div
                    className="timeline-event-pill"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleBookingClick(booking, room.name);
                    }}
                    style={{
                        width: `calc(${displaySpan * 100}% + ${(displaySpan - 1)}px - 14px)`,
                        backgroundColor: style.bg,
                        color: style.text,
                        borderColor: style.border,
                        borderWidth: '2px',
                        borderStyle: booking.status === 'UPCOMING' ? 'dashed' : 'solid',

                    }}
                    title={`${booking.guest} • ${booking.status} • ${booking.span} nights • Bed ${booking.bedIndex + 1}`}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
                        <span className="event-guest" style={{ flex: 1, minWidth: 0 }}>{booking.guest}</span>
                        {genderIcon && (
                            <span style={{
                                fontSize: '1.5rem',
                                fontWeight: '700',
                                opacity: 0.9
                            }}>
                                {genderIcon}
                            </span>
                        )}
                        {bookingStartedBeforeTimeline && isTimelineStart && (
                            <span style={{ fontSize: '0.7em', marginLeft: '4px' }}>«</span>
                        )}
                    </div>
                </div>
            );
        }
        return null; // Spacer
    };

    return (
        <div className="timeline-container">
            {/* Color Legend */}
            <div style={{
                padding: '16px 20px',
                background: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
                flexWrap: 'wrap'
            }}>
                <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>Legend:</span>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '32px',
                            height: '20px',
                            background: ACTIVE_STYLES.DEFAULT.bg,
                            border: `2px solid ${ACTIVE_STYLES.DEFAULT.border}`,
                            borderRadius: '4px'
                        }} />
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Active</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '32px',
                            height: '20px',
                            background: UPCOMING_STYLES.DEFAULT.bg,
                            border: `2px dashed ${UPCOMING_STYLES.DEFAULT.border}`,
                            borderRadius: '4px'
                        }} />
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Upcoming</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '32px',
                            height: '20px',
                            background: ACTIVE_STYLES.MALE.bg,
                            border: `2px solid ${ACTIVE_STYLES.MALE.border}`,
                            borderRadius: '4px'
                        }} />
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Male</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '32px',
                            height: '20px',
                            background: ACTIVE_STYLES.FEMALE.bg,
                            border: `2px solid ${ACTIVE_STYLES.FEMALE.border}`,
                            borderRadius: '4px'
                        }} />
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Female</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '32px',
                            height: '20px',
                            background: ACTIVE_STYLES.GROUP.bg,
                            border: `2px solid ${ACTIVE_STYLES.GROUP.border}`,
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            <span style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                fontSize: '0.5rem',
                                fontWeight: '700',
                                color: '#fff'
                            }}>G</span>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Group Booking</span>
                    </div>
                </div>
            </div>

            <div className="timeline-scroll-wrapper">

                {/* 1. Header Row (Sticky Top) */}
                <div className="timeline-header-row">
                    {/* Fixed Corner */}
                    <div className="timeline-corner-cell">
                        Room Type
                    </div>
                    {/* Dates */}
                    {dates.map((date, i) => (
                        <div key={i} className={`timeline-date-cell ${isToday(date) ? 'today' : ''}`}>
                            <span className="day-name">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            <span className="day-number">{date.getDate()}</span>
                        </div>
                    ))}
                </div>

                {/* 2. Groups Loop */}
                {groups.map(group => (
                    <div key={group.id} className="group-wrapper">

                        {/* Group Header (Sticky Left) */}
                        <div className="timeline-group-row">
                            <div className="timeline-group-label" onClick={() => toggleGroup(group.id)}>
                                {group.collapsed ? <ChevronRight size={16} style={{ marginRight: 8 }} /> : <ChevronDown size={16} style={{ marginRight: 8 }} />}
                                <span>{group.name}</span>
                                <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#64748b', background: '#e2e8f0', padding: '2px 6px', borderRadius: 4 }}>
                                    {group.rooms.length}
                                </span>
                            </div>
                            {/* Empty space matching columns length if needed, or just a flex filler */}
                            <div className="timeline-group-content-area">
                                {group.collapsed && <span>{group.totalRooms} Rooms Available matching check-in criteria...</span>}
                            </div>
                        </div>

                        {/* Room Rows */}
                        {!group.collapsed && group.rooms.flatMap(room => {
                            const isShared = room.isShared;
                            const capacity = isShared ? (room.capacity || 1) : 1;

                            // Generate an array of row indices based on capacity
                            return Array.from({ length: capacity }, (_, bedIndex) => (
                                <div key={`${room.id}-${bedIndex}`} className="timeline-room-row">
                                    {/* Room Label (Sticky Left) */}
                                    <div className="timeline-room-label">
                                        {bedIndex === 0 ? (
                                            <>
                                                <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem', marginBottom: '4px' }}>
                                                    {room.name} {isShared && <span style={{ fontSize: '0.7em', color: '#eab308' }}>★</span>}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                                    {room.type} {isShared && `• Shared (${capacity} beds)`}
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{ paddingLeft: '10px', display: 'flex', alignItems: 'center', height: '100%', color: '#94a3b8', fontSize: '0.8rem' }}>
                                                <span style={{ marginRight: 6 }}>└</span> Bed {String.fromCharCode(65 + bedIndex)}
                                            </div>
                                        )}
                                    </div>

                                    {/* Grid Cells */}
                                    {dates.map((date, i) => {
                                        const booking = getBookingOnDate(room, date, bedIndex);
                                        return (
                                            <div key={i} className={`timeline-cell ${isWeekend(date) ? 'weekend' : ''}`}>
                                                {renderBookingPill(booking, date, room)}
                                            </div>
                                        );
                                    })}
                                </div>
                            ));
                        })}
                    </div>
                ))}

            </div>

            {/* Booking Details Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.1rem' }}>
                        <span style={{ background: '#eff6ff', padding: '8px', borderRadius: '50%', color: '#1d4ed8', display: 'flex' }}>
                            <User size={20} />
                        </span>
                        <span>Booking Details</span>
                    </div>
                }
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={450}
                centered
                zIndex={9999}
            >
                {selectedBooking && (
                    <div className="booking-modal-content" style={{ marginTop: '16px' }}>
                        <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '20px' }}>
                            <h3 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', color: '#0f172a' }}>{selectedBooking.guest}</h3>
                            <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '12px' }}>
                                {selectedBooking.roomName} • Bed {selectedBooking.bedIndex !== undefined ? String.fromCharCode(65 + selectedBooking.bedIndex) : 'N/A'} • {selectedBooking.status}
                            </div>

                            <div style={{
                                background: '#f0f9ff',
                                border: '1px solid #bae6fd',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#0369a1' }}>
                                    {selectedBooking.isGroupBooking ? 'Order ID (Group)' : 'Order ID'}
                                </span>
                                <span style={{ fontSize: '0.8rem', color: '#0c4a6e', fontFamily: 'monospace' }}>
                                    {selectedBooking.orderUniqueId || selectedBooking.orderId || selectedBooking.id}
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                            <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #dcfce7' }}>
                                <span style={{ fontSize: '0.75rem', color: '#166534', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                                    <Calendar size={12} /> Check-in
                                </span>
                                <div style={{ fontWeight: '600', color: '#14532d' }}>{selectedBooking.checkIn}</div>
                            </div>
                            <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '8px', border: '1px solid #fee2e2' }}>
                                <span style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                                    <Calendar size={12} /> Check-out
                                </span>
                                <div style={{ fontWeight: '600', color: '#7f1d1d' }}>{selectedBooking.checkOut}</div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <h5 style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Info size={14} /> Special Requests / Notes
                            </h5>
                            <p style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '12px', borderRadius: '8px', fontSize: '0.9rem', color: '#334155', margin: 0 }}>
                                {selectedBooking.notes || 'No special requests.'}
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                            <Button
                                type="primary"
                                onClick={handleChangeRoom}
                                style={{ flex: 1 }}
                            >
                                Change Room
                            </Button>
                            <Button
                                type="default"
                                onClick={handleUpgrade}
                                style={{ flex: 1, borderColor: '#3b82f6', color: '#3b82f6' }}
                            >
                                Upgrade
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Available Rooms / Upgrade Modal */}
            <Modal
                title={isUpgradeMode ? 'Upgrade Room' : 'Change Room'}
                open={isAvailableRoomsModalVisible}
                onCancel={handleCloseAvailableRoomsModal}
                footer={[
                    <Button key="close" onClick={handleCloseAvailableRoomsModal}>
                        Close
                    </Button>
                ]}
                width={700}
                centered
                zIndex={10000}
            >
                {availableRoomsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '16px', color: '#64748b' }}>Loading available rooms...</p>
                    </div>
                ) : availableRoomsError ? (
                    <Alert
                        message="Error"
                        description={availableRoomsError}
                        type="error"
                        showIcon
                    />
                ) : availableRooms ? (
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {isUpgradeMode ? (
                            // Upgrade Options Display
                            availableRooms.length > 0 ? (
                                availableRooms.map((occupancyGroup, idx) => (
                                    <div key={idx} style={{ marginBottom: '24px' }}>
                                        <h4 style={{ color: '#0f172a', marginBottom: '12px' }}>
                                            {occupancyGroup.occupancyCode}
                                        </h4>
                                        {occupancyGroup.options.map((option, optIdx) => (
                                            <Card
                                                key={optIdx}
                                                style={{ marginBottom: '16px' }}
                                                hoverable
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                                                    <div>
                                                        <h5 style={{ margin: 0, color: '#0f172a' }}>{option.roomTypeName}</h5>
                                                        <Tag color="blue" style={{ marginTop: '4px' }}>Rank: {option.roomTypeRank}</Tag>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#0f172a' }}>
                                                            {option.currency} {option.pricePerDay}/day
                                                        </div>
                                                        <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                                                            Total: {option.currency} {option.totalPrice}
                                                        </div>
                                                        <Tag color="green" style={{ marginTop: '4px' }}>
                                                            +{option.currency} {option.priceDelta}
                                                        </Tag>

                                                        {option.memberDayCalculation && (
                                                            <div style={{ marginTop: '8px', textAlign: 'right' }}>
                                                                <Tooltip title={!option.memberDayCalculation.hasEnoughDays ? "Not enough member days available" : "Use member days for upgrade"}>
                                                                    <Checkbox
                                                                        disabled={!option.memberDayCalculation.hasEnoughDays}
                                                                        checked={memberDaysSelection[`${option.roomTypeId}_${occupancyGroup.occupancyModeId}`] || false}
                                                                        onChange={(e) => handleUseMemberDaysChange(option.roomTypeId, occupancyGroup.occupancyModeId, e.target.checked)}
                                                                    >
                                                                        <span style={{ fontWeight: 500 }}>Use Member Days</span>
                                                                    </Checkbox>
                                                                </Tooltip>
                                                                {memberDaysSelection[`${option.roomTypeId}_${occupancyGroup.occupancyModeId}`] && (
                                                                    <div style={{ fontSize: '0.8rem', color: '#0369a1', marginTop: '2px' }}>
                                                                        Cost: {option.memberDayCalculation.daysRequired} Days
                                                                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>
                                                                            (Aval: {option.memberDayCalculation.availableDays})
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <Divider style={{ margin: '12px 0' }} />
                                                <div>
                                                    <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#475569' }}>Available Rooms (click to select):</p>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                        {option.availableRooms.map((room) => (
                                                            <Tag
                                                                key={room._id}
                                                                color="cyan"
                                                                style={{ cursor: 'pointer', padding: '4px 8px' }}
                                                                onClick={() => handleRoomSelection(room, occupancyGroup, option)}
                                                            >
                                                                {room.roomNo}
                                                            </Tag>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                <Alert
                                    message="No Upgrade Options"
                                    description="There are no upgrade options available for this booking."
                                    type="info"
                                    showIcon
                                />
                            )
                        ) : (
                            // Change Room Display
                            availableRooms.length > 0 ? (
                                <List
                                    dataSource={availableRooms}
                                    renderItem={(room) => (
                                        <List.Item
                                            style={{
                                                padding: '16px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                marginBottom: '12px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                            onClick={() => handleRoomSelection(room)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f8fafc';
                                                e.currentTarget.style.borderColor = '#3b82f6';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.borderColor = '#e2e8f0';
                                            }}
                                        >
                                            <div style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <h4 style={{ margin: 0, color: '#0f172a' }}>{room.roomNo}</h4>
                                                    <Tag color="blue">Available</Tag>
                                                </div>
                                                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>Click to select this room</p>
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Alert
                                    message="No Available Rooms"
                                    description="There are no available rooms for change at this time."
                                    type="info"
                                    showIcon
                                />
                            )
                        )}
                    </div>
                ) : (
                    <Alert
                        message="No Data"
                        description="Please click on Change Room or Upgrade to fetch available options."
                        type="info"
                        showIcon
                    />
                )}
            </Modal>
        </div>
    );
};

export default TimelineScheduler;
